import { clsx } from 'clsx';
import React, { MutableRefObject } from 'react';

import { User } from '@utils/backend-api/currentUser';
import { Newsletter } from '@utils/backend-api/newsletters';
import { SanitizedProfileSettingsParams } from '@utils/backend-api/profileSettings';
import { SubscribeSource } from '@utils/backend-api/userOnboarding';
import { ProfileParams } from '@utils/backend-api/userProfile';

import StepFinal from './StepFinal';
import StepNewsletter from './StepNewsletter';
import StepProfile from './StepProfile';
import StepTopics from './StepTopics';
import { OnboardingSteps, Topic } from './interface';

interface StepContentProps {
  step: OnboardingSteps;
  user: User;
  topics: Topic[];
  newsletters: Newsletter[];
  stepSaveRef: MutableRefObject<(() => void | Promise<void>) | null>;
  onStepComplete: () => void;
  saveProfile: (
    values: SanitizedProfileSettingsParams,
    options?: { onSuccess?: () => void }
  ) => void;
  updateProfile: (profile: ProfileParams) => Promise<Record<string, never>>;
  subscribeAsync: (params: { newsletter_id: number | string }) => Promise<unknown>;
  unsubscribeAsync: (params: { newsletter_id: number | string }) => Promise<unknown>;
  subscribeSource?: SubscribeSource;
}

const StepContent = ({
  step,
  user,
  topics,
  newsletters,
  stepSaveRef,
  onStepComplete,
  saveProfile,
  updateProfile,
  subscribeAsync,
  unsubscribeAsync,
  subscribeSource,
}: StepContentProps) => {
  const renderStep = () => {
    switch (step) {
      case OnboardingSteps.PROFILE:
        return (
          <StepProfile
            user={user}
            saveProfile={saveProfile}
            onStepComplete={onStepComplete}
            stepSaveRef={stepSaveRef}
            subscribeSource={subscribeSource}
          />
        );
      case OnboardingSteps.TOPICS:
        return (
          <StepTopics
            topics={topics ?? []}
            isProSubscriber={user.is_pro_subscriber}
            updateProfile={updateProfile}
            onStepComplete={onStepComplete}
            stepSaveRef={stepSaveRef}
          />
        );
      case OnboardingSteps.NEWSLETTERS:
        return (
          <StepNewsletter
            newsletters={newsletters ?? []}
            onStepComplete={onStepComplete}
            stepSaveRef={stepSaveRef}
            subscribeAsync={subscribeAsync}
            unsubscribeAsync={unsubscribeAsync}
          />
        );
      case OnboardingSteps.FINAL:
        return <StepFinal isProSubscriber={user.is_pro_subscriber} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={clsx({
        'pt-32 md:pt-72': step === OnboardingSteps.FINAL,
      })}
    >
      <div
        className={clsx('flex', {
          'justify-center pt-16 md:pt-48': step !== OnboardingSteps.FINAL,
          'justify-start pt-0 md:justify-center md:pt-48': step === OnboardingSteps.FINAL,
        })}
      >
        {renderStep()}
      </div>
    </div>
  );
};

export default StepContent;
